import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import arcaSeal from '../../assets/ARCA SEAL.png';
import arcaLogo from '../../assets/arca_logo.png';
import dorothySignature from '../../assets/DorSignature.png';

const MembershipCertificate = ({ membershipStatus, membershipType, onClose }) => {
    const [certificateData, setCertificateData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const certificateRef = useRef(null);

    // Vendors can always access their certificate; other members need active status
    const canAccessCertificate = membershipType === 'vendor' || membershipStatus === 'active';

    useEffect(() => {
        if (canAccessCertificate) {
            fetchCertificateData();
        }
    }, [membershipStatus, membershipType]);

    const fetchCertificateData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken') || localStorage.getItem('userToken');

            console.log('🎓 [Certificate] Fetching certificate data...');
            console.log('🔑 [Certificate] Token:', token ? 'Found' : 'NOT FOUND');

            const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE}/memberships/certificate`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('📡 [Certificate] Response Status:', response.status);
            console.log('📡 [Certificate] Response OK:', response.ok);

            const data = await response.json();
            console.log('📋 [Certificate] Response Data:', data);

            if (data.success) {
                console.log('✅ [Certificate] Certificate data loaded successfully:', data.data);
                setCertificateData(data.data);
            } else {
                console.error('❌ [Certificate] API returned error:', data.message);
                setError(data.message || 'Failed to fetch certificate data');
            }
        } catch (error) {
            console.error('❌ [Certificate] Error fetching certificate:', error);
            setError('Failed to load certificate data');
        } finally {
            setLoading(false);
        }
    };

    const generatePDF = async () => {
        if (!certificateData) return;

        setGenerating(true);

        try {
            const element = certificateRef.current;

            // Use html2canvas to capture the certificate as an image
            const canvas = await html2canvas(element, {
                scale: 2,
                backgroundColor: '#ffffff',
                logging: false
            });

            // Create PDF in portrait orientation
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210; // A4 portrait width in mm
            const imgHeight = 297; // A4 portrait height in mm

            const imgData = canvas.toDataURL('image/png');
            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

            // Save PDF with filename format
            pdf.save(`ARCA_Membership_Certificate_${certificateData.membership_id}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setGenerating(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading certificate...</span>
                </div>
                <p className="mt-3 text-muted">Loading your certificate...</p>
            </div>
        );
    }

    if (error || !canAccessCertificate) {
        return (
            <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error || 'Certificate is only available for active members. Please ensure your membership is activated by an admin.'}
            </div>
        );
    }

    if (!certificateData) {
        return (
            <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                No certificate data available.
            </div>
        );
    }

    return (
        <div className="certificate-container">
            {/* Certificate Preview */}
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Membership Certificate Preview</h5>
                    <div className="d-flex gap-2">
                        <button
                            onClick={generatePDF}
                            disabled={generating}
                            className="btn btn-success"
                        >
                            <i className={`fas ${generating ? 'fa-spinner fa-spin' : 'fa-download'} me-2`}></i>
                            {generating ? 'Generating PDF...' : 'Download Certificate (PDF)'}
                        </button>
                        {onClose && (
                            <button onClick={onClose} className="btn btn-secondary">
                                Close
                            </button>
                        )}
                    </div>
                </div>

                {/* Scrollable container for certificate preview */}
                <div style={{
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    padding: '20px',
                    backgroundColor: '#f5f5f5'
                }}>
                    {/* Certificate Design - PORTRAIT orientation, NO TRANSFORM for sharp preview */}
                    <div
                        ref={certificateRef}
                        style={{
                            width: '210mm',
                            height: '297mm',
                            backgroundColor: 'white',
                            position: 'relative',
                            fontFamily: 'Georgia, serif',
                            margin: '0 auto',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                        }}
                    >
                        {/* Background Watermark */}
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            opacity: 0.03,
                            zIndex: 0,
                            pointerEvents: 'none'
                        }}>
                            <img src={arcaLogo} alt="Watermark" style={{ width: '600px' }} />
                        </div>

                        {/* Top Right Corner */}
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 0,
                            height: 0,
                            borderStyle: 'solid',
                            borderWidth: '0 150px 150px 0',
                            borderColor: 'transparent #4A5F4A transparent transparent',
                            zIndex: 0
                        }} />

                        {/* Bottom Left Corner */}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: 0,
                            height: 0,
                            borderStyle: 'solid',
                            borderWidth: '150px 0 0 150px',
                            borderColor: 'transparent transparent transparent #6B8E23',
                            zIndex: 0
                        }} />

                        {/* Content - Aggressively reduced spacing */}
                        <div style={{ position: 'relative', zIndex: 1, padding: '30px 60px 20px 60px', display: 'flex', flexDirection: 'column' }}>
                            {/* ARCA Logo */}
                            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                <img src={arcaLogo} alt="ARCA Logo" style={{ height: '100px' }} />
                            </div>

                            {/* Title */}
                            <h1 style={{
                                textAlign: 'center',
                                fontSize: '48px',
                                fontWeight: 'bold',
                                color: '#000',
                                margin: '0 0 18px 0'
                            }}>
                                Membership Certificate
                            </h1>

                            {/* Body */}
                            <div style={{ textAlign: 'center', fontSize: '18px', color: '#000', lineHeight: '1.4' }}>
                                <p style={{ margin: '0 0 12px 0' }}>This is to certify that</p>

                                <div style={{
                                    borderBottom: '2px solid #000',
                                    width: '500px',
                                    margin: '0 auto 6px auto',
                                    paddingBottom: '5px',
                                    fontSize: '22px',
                                    minHeight: '30px'
                                }}>
                                    {certificateData.membership_type === 'vendor' && certificateData.company_name
                                        ? certificateData.company_name
                                        : certificateData.full_name}
                                </div>

                                <p style={{ fontSize: '14px', fontStyle: 'italic', margin: '0 0 12px 0' }}>
                                    {certificateData.membership_type === 'vendor'
                                        ? '(Company / Business Name)'
                                        : '(Full Name of Member)'}
                                </p>

                                {/* For vendors: also show the Contact Person's name below */}
                                {certificateData.membership_type === 'vendor' && (
                                    <p style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
                                        Represented by: <strong>{certificateData.full_name}</strong>
                                    </p>
                                )}

                                <p style={{ margin: '0 0 12px 0', lineHeight: '1.3' }}>
                                    {certificateData.membership_type === 'vendor'
                                        ? 'is a duly registered Vendor Partner of the Africa Respiratory Care Association (ARCA).'
                                        : 'is a duly registered member of the Africa Respiratory Care Association (ARCA).'}
                                </p>

                                <p style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: 'bold' }}>
                                    Membership ID: {certificateData.membership_id}
                                </p>

                                {certificateData.membership_type === 'vendor' && certificateData.vendor_tier && (
                                    <p style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 'bold', color: '#556B2F' }}>
                                        {certificateData.vendor_tier} Partner
                                    </p>
                                )}

                                <p style={{ margin: '0 0 12px 0' }}>
                                    This membership is valid from {certificateData.start_month} {certificateData.start_year} to {certificateData.end_month} {certificateData.end_year}.
                                </p>

                                <p style={{ margin: '0 0 8px 0', fontSize: '16px', lineHeight: '1.4', padding: '0 30px' }}>
                                    {certificateData.membership_type === 'vendor'
                                        ? 'This certificate is issued in recognition of the organization\'s commitment to supporting and advancing respiratory care education, professional practice, and advocacy across Africa and beyond.'
                                        : 'This certificate is issued in recognition of the member\'s commitment to advancing respiratory care education, professional practice, and advocacy across Africa and beyond.'}
                                </p>
                            </div>

                            {/* Signature and Seal */}
                            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                {/* Signature - NO LINE */}
                                <div style={{ textAlign: 'left' }}>
                                    <img src={dorothySignature} alt="Signature" style={{ height: '55px', marginBottom: '5px' }} />
                                    <div style={{ paddingTop: '0px', width: '220px' }}>
                                        <p style={{ margin: '0', fontSize: '14px', fontWeight: 'bold', lineHeight: '1.3' }}>
                                            Dorothy H. Bendah
                                        </p>
                                        <p style={{ margin: '0', fontSize: '13px', fontWeight: 'bold', lineHeight: '1.3' }}>
                                            PRESIDENT
                                        </p>
                                    </div>
                                </div>

                                {/* ARCA Seal */}
                                <div style={{ textAlign: 'center' }}>
                                    <img src={arcaSeal} alt="ARCA Seal" style={{ width: '110px', marginBottom: '8px' }} />
                                    <p style={{ margin: '0', fontSize: '13px', color: '#6B8E23', fontWeight: '600' }}>
                                        www.africaresp.org
                                    </p>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ textAlign: 'center', marginTop: '5px' }}>
                                <p style={{ margin: '0', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                                    This certificate is electronically generated and verifiable.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Close scrollable container */}
            </div>

            <style jsx>{`
        @media print {
          .certificate-container > div:first-child > div:first-child {
            display: none;
          }
          
          .certificate-container > div > div[ref] {
            transform: scale(1) !important;
            margin: 0 !important;
          }
        }
      `}</style>
        </div>
    );
};

export default MembershipCertificate;
